import React from 'react';

import { FilterHookApi, FilterHookOptions } from '../types';

export type UseTextFilterOptions = Omit<FilterHookOptions<string>, 'getId'>;

export type UseTextFilter = FilterHookApi<string>;

export const useTextFilter = (options?: UseTextFilterOptions): UseTextFilter => {
    const [selected, setSelected] = React.useState<string | undefined>((options && options.defaultSelected) || '');
    const [selection, setSelection] = React.useState<string | undefined>(selected);

    const { defaultSelected, hasDynamicDefaultSelected } = options || {};

    /**
     * Syncs internal state with externally-controlled defaults. This covers both cases:
     * new defaults being applied AND defaults being removed (defaultSelected becoming undefined),
     * ensuring the UI never shows stale filter values.
     */
    React.useEffect(() => {
        if (hasDynamicDefaultSelected) {
            setSelected(defaultSelected || '');
            setSelection(defaultSelected || '');
        }
    }, [defaultSelected, hasDynamicDefaultSelected]);

    const onSelected = (sel: string | undefined) => {
        setSelection(sel);
    };

    const onFilter = () => {
        setSelected(selection);

        return selection;
    };

    const onClearSelection = () => {
        setSelection(selected);
    };

    const onClearSelected = () => {
        setSelected('');
        setSelection('');
    };

    const onResetFilters = () => {
        if (options?.defaultSelected) {
            setSelected(options?.defaultSelected);
            setSelection(options?.defaultSelected);
        } else {
            onClearSelected();
        }
    };

    return {
        onSelected,
        onFilter,
        selected,
        selection,
        onClearSelection,
        onClearSelected,
        onResetFilters,
    };
};
